import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  db, collection, getDocs, addDoc, deleteDoc, doc, updateDoc,
  serverTimestamp, storage, ref, uploadBytes, getDownloadURL
} from '../../firebase/config';
import {
  Plus, Clock, X, Camera, Video, Sparkles,
  Scissors, Droplets, Eye, Heart, Briefcase, Settings,
  CreditCard, Banknote, Wallet, Info, Trash2,
  MapPin, Car, Store, Check
} from 'lucide-react';

const MAX_MEDIA = 5;
const MAX_IMAGE_DIMENSION = 1600;
const IMAGE_QUALITY = 0.82;

// ============================================================
// IMAGE COMPRESSION
// ============================================================
const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    // Only compress images; pass videos through
    if (!file.type.startsWith('image/')) {
      resolve(file);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;

        // Scale down if larger than max dimension
        if (width > MAX_IMAGE_DIMENSION || height > MAX_IMAGE_DIMENSION) {
          if (width >= height) {
            height = Math.round((height * MAX_IMAGE_DIMENSION) / width);
            width = MAX_IMAGE_DIMENSION;
          } else {
            width = Math.round((width * MAX_IMAGE_DIMENSION) / height);
            height = MAX_IMAGE_DIMENSION;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve(file);
              return;
            }
            // Build a new File so Firebase Storage gets a proper name
            const ext = 'jpg';
            const baseName = file.name.replace(/\.[^.]+$/, '');
            const compressedFile = new File(
              [blob],
              `${baseName}.${ext}`,
              { type: 'image/jpeg', lastModified: Date.now() }
            );
            resolve(compressedFile);
          },
          'image/jpeg',
          IMAGE_QUALITY
        );
      };
      img.onerror = () => resolve(file);
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// ============================================================
// SUGGESTED ADD-ONS BY SPECIALTY
// ============================================================
const SUGGESTED_ADDONS = {
  lash: [
    { name: 'Volume upgrade', defaultPrice: 150 },
    { name: 'Extra length', defaultPrice: 80 },
    { name: 'Bottom lashes', defaultPrice: 100 },
    { name: 'Lash tint', defaultPrice: 60 },
    { name: 'Lash removal', defaultPrice: 100 },
    { name: 'Brow shaping', defaultPrice: 70 },
  ],
  nail: [
    { name: 'Nail art', defaultPrice: 50 },
    { name: 'Extra length', defaultPrice: 60 },
    { name: 'Gel top coat', defaultPrice: 40 },
    { name: 'French tips', defaultPrice: 70 },
    { name: 'Rhinestones', defaultPrice: 30 },
    { name: 'Nail repair', defaultPrice: 25 },
  ],
  hair: [
    { name: 'Deep treatment', defaultPrice: 120 },
    { name: 'Blow-dry', defaultPrice: 80 },
    { name: 'Scalp massage', defaultPrice: 60 },
    { name: 'Gloss', defaultPrice: 100 },
    { name: 'Toner', defaultPrice: 90 },
    { name: 'Extra length', defaultPrice: 150 },
  ],
  skin: [
    { name: 'LED therapy', defaultPrice: 150 },
    { name: 'Extra extraction', defaultPrice: 80 },
    { name: 'Serum boost', defaultPrice: 100 },
    { name: 'Collagen mask', defaultPrice: 120 },
    { name: 'Dermaplaning add-on', defaultPrice: 180 },
    { name: 'Eye treatment', defaultPrice: 90 },
  ],
  spa: [
    { name: 'Hot stones', defaultPrice: 100 },
    { name: 'Aromatherapy', defaultPrice: 60 },
    { name: 'Deep tissue upgrade', defaultPrice: 120 },
    { name: 'Scalp massage', defaultPrice: 70 },
    { name: 'Extended time', defaultPrice: 150 },
    { name: 'CBD oil', defaultPrice: 100 },
  ],
  makeup: [
    { name: 'False lashes', defaultPrice: 80 },
    { name: 'Airbrush finish', defaultPrice: 150 },
    { name: 'Touch-up kit', defaultPrice: 120 },
    { name: 'Hair styling', defaultPrice: 200 },
    { name: 'Extra face prep', defaultPrice: 70 },
  ],
};

const getSuggestedAddonsFor = (specialization = '') => {
  const s = specialization.toLowerCase();
  if (s.includes('lash') || s.includes('brow')) return SUGGESTED_ADDONS.lash;
  if (s.includes('nail') || s.includes('manicure') || s.includes('pedicure')) return SUGGESTED_ADDONS.nail;
  if (s.includes('hair') || s.includes('stylist') || s.includes('barber')) return SUGGESTED_ADDONS.hair;
  if (s.includes('skin') || s.includes('esthetician') || s.includes('facial')) return SUGGESTED_ADDONS.skin;
  if (s.includes('spa') || s.includes('massage')) return SUGGESTED_ADDONS.spa;
  if (s.includes('makeup') || s.includes('mua')) return SUGGESTED_ADDONS.makeup;
  return [
    { name: 'Extra time', defaultPrice: 80 },
    { name: 'Premium product', defaultPrice: 100 },
    { name: 'Aftercare kit', defaultPrice: 120 },
  ];
};

const ProfessionalServicesPage = () => {
  const { user, userData } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '', category: '', duration: '45 min', price: '',
    variablePrice: false, description: '', addons: [],
    paymentPolicy: 'inherit',
    depositPercent: 30,
    hasOptions: false,
    options: [],
    locationMode: 'salon',
    transportFee: 0,
  });

  const [newAddonName, setNewAddonName] = useState('');
  const [newAddonPrice, setNewAddonPrice] = useState('');

  const specialization = userData?.specialization?.toLowerCase() || '';
  const proDefaultPolicy = userData?.defaultPaymentPolicy || 'after';
  const proDefaultDeposit = userData?.defaultDepositPercent || 30;

  const suggestedAddons = getSuggestedAddonsFor(userData?.specialization || '');

  const getSpecializationIcon = () => {
    if (specialization.includes('lash')) return <Eye size={20} className="text-[#AF8BD4]" />;
    if (specialization.includes('nail')) return <Sparkles size={20} className="text-[#AF8BD4]" />;
    if (specialization.includes('hair')) return <Scissors size={20} className="text-[#AF8BD4]" />;
    if (specialization.includes('skin') || specialization.includes('esthetician')) return <Droplets size={20} className="text-[#AF8BD4]" />;
    if (specialization.includes('spa') || specialization.includes('massage')) return <Heart size={20} className="text-[#AF8BD4]" />;
    return <Briefcase size={20} className="text-[#AF8BD4]" />;
  };

  const getCategoryOptions = () => {
    const categories = {
      'lash': ['Classic Lashes', 'Hybrid Lashes', 'Volume Lashes', 'Mega Volume', 'Lash Lift', 'Brow Lamination', 'Lash Removal'],
      'nail': ['Manicure', 'Pedicure', 'Gel Nails', 'Acrylic Nails', 'Dip Powder', 'Nail Art', 'Nail Removal'],
      'hair': ['Women\'s Haircut', 'Men\'s Haircut', 'Coloring', 'Highlights', 'Balayage', 'Blow-dry', 'Extensions', 'Treatment'],
      'skin': ['Facial', 'Chemical Peel', 'Microdermabrasion', 'Extractions', 'LED Therapy', 'Dermaplaning', 'Hydrafacial'],
      'spa': ['Swedish Massage', 'Deep Tissue', 'Hot Stone', 'Sports Massage', 'Thai Massage', 'Shiatsu'],
      'makeup': ['Bridal Makeup', 'Editorial Makeup', 'Everyday Makeup', 'Airbrush Makeup', 'Special Effects']
    };
    for (const [key, options] of Object.entries(categories)) {
      if (specialization.includes(key)) return options;
    }
    return ['General Service', 'Consultation', 'Treatment', 'Package'];
  };

  const categoryOptions = getCategoryOptions();
  const durations = ['15 min', '30 min', '45 min', '1 hr', '1.5 hr', '2 hr+'];

  useEffect(() => { loadServices(); }, []);

  const loadServices = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'services'));
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setServices(data.filter(s => s.professionalId === user?.uid));
    } catch (error) {
      console.error('Error loading services:', error);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Media select with compression ----------
  const handleFilesSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const remainingSlots = MAX_MEDIA - mediaItems.length;
    if (remainingSlots <= 0) {
      alert(`You can upload up to ${MAX_MEDIA} files`);
      return;
    }

    const toAdd = files.slice(0, remainingSlots);

    for (const file of toAdd) {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');
      if (!isVideo && !isImage) continue;

      const limit = isVideo ? 25 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > limit) {
        alert(`${file.name} is too large (max ${isVideo ? '25MB' : '5MB'})`);
        continue;
      }

      // Compress images before storing in state
      let processedFile = file;
      if (isImage) {
        try {
          processedFile = await compressImage(file);
        } catch (err) {
          console.warn('Compression failed, using original:', err);
        }
      }

      // Build preview URL from the compressed file
      const previewUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.readAsDataURL(processedFile);
      });

      setMediaItems((prev) => [
        ...prev,
        {
          file: processedFile,
          url: previewUrl,
          type: isVideo ? 'video' : 'image',
          isNew: true,
        },
      ]);
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeMedia = (index) => {
    setMediaItems((prev) => prev.filter((_, i) => i !== index));
  };

  const makeCover = (index) => {
    setMediaItems((prev) => {
      const next = [...prev];
      const [picked] = next.splice(index, 1);
      next.unshift(picked);
      return next;
    });
  };

  const uploadAllMedia = async () => {
    const result = [];
    for (const item of mediaItems) {
      if (item.isNew && item.file) {
        const storageRef = ref(storage, `services/${user.uid}/${Date.now()}_${item.file.name}`);
        await uploadBytes(storageRef, item.file, {
          contentType: item.file.type || 'image/jpeg',
        });
        const url = await getDownloadURL(storageRef);
        result.push({ url, type: item.type });
      } else {
        result.push({ url: item.url, type: item.type });
      }
    }
    return result;
  };

  // ---------- Service options helpers ----------
  const addOption = () => {
    setFormData(prev => ({
      ...prev,
      options: [
        ...prev.options,
        {
          id: `opt_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          name: '',
          price: '',
          duration: prev.duration || '45 min',
        },
      ],
    }));
  };

  const updateOption = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map(o => o.id === id ? { ...o, [field]: value } : o),
    }));
  };

  const removeOption = (id) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter(o => o.id !== id),
    }));
  };

  // ---------- Add-ons helpers ----------
  const addAddon = (name, price) => {
    const trimmedName = (name || '').trim();
    if (!trimmedName) return;
    const numPrice = parseFloat(price) || 0;
    if (numPrice <= 0) {
      alert('Add-on price must be greater than 0');
      return;
    }
    const exists = (formData.addons || []).some(a => a.name.toLowerCase() === trimmedName.toLowerCase());
    if (exists) {
      alert('You already have this add-on');
      return;
    }
    setFormData(prev => ({
      ...prev,
      addons: [
        ...(prev.addons || []),
        {
          id: `addon_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
          name: trimmedName,
          price: numPrice,
        },
      ],
    }));
  };

  const addSuggestedAddon = (suggestion) => {
    addAddon(suggestion.name, suggestion.defaultPrice);
  };

  const updateAddon = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      addons: (prev.addons || []).map(a =>
        a.id === id
          ? { ...a, [field]: field === 'price' ? (parseFloat(value) || 0) : value }
          : a
      ),
    }));
  };

  const removeAddon = (id) => {
    setFormData(prev => ({
      ...prev,
      addons: (prev.addons || []).filter(a => a.id !== id),
    }));
  };

  const handleCustomAddonSubmit = () => {
    if (!newAddonName.trim()) {
      alert('Enter an add-on name');
      return;
    }
    addAddon(newAddonName, newAddonPrice);
    setNewAddonName('');
    setNewAddonPrice('');
  };

  // ---------- Payment policy helpers ----------
  const getEffectivePolicy = (form) => {
    if (form.paymentPolicy === 'inherit') return proDefaultPolicy;
    return form.paymentPolicy;
  };

  const getEffectiveDepositPercent = (form) => {
    if (form.paymentPolicy === 'inherit') return proDefaultDeposit;
    return form.depositPercent || 30;
  };

  // ---------- Submit ----------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const uploadedMedia = await uploadAllMedia();

      const price = parseFloat(formData.price) || 0;
      const effectivePolicy = getEffectivePolicy(formData);
      const effectiveDeposit = getEffectiveDepositPercent(formData);

      const sanitizedOptions = (formData.options || [])
        .filter(o => o.name?.trim() && o.price !== '')
        .map(o => ({
          id: o.id,
          name: o.name.trim(),
          price: parseFloat(o.price) || 0,
          duration: o.duration || '45 min',
        }))

      const sanitizedAddons = (formData.addons || [])
        .filter(a => a.name?.trim() && Number(a.price) > 0)
        .map(a => ({
          id: a.id || `addon_${Math.random().toString(36).slice(2, 8)}`,
          name: a.name.trim(),
          price: Number(a.price),
        }));

      const serviceData = {
        title: formData.title,
        category: formData.category,
        duration: formData.duration,
        price,
        variablePrice: formData.variablePrice,
        description: formData.description,
        addons: sanitizedAddons,
        media: uploadedMedia,
        mediaLegacy: uploadedMedia[0] || null,
        professionalId: user?.uid,
        specialization: userData?.specialization || '',
        active: true,

        paymentPolicy: formData.paymentPolicy,
        depositPercent: effectiveDeposit,

        options: sanitizedOptions,
        hasOptions: sanitizedOptions.length > 0,

        locationMode: formData.locationMode || 'salon',
        transportFee: (formData.locationMode === 'mobile' || formData.locationMode === 'both')
          ? (parseFloat(formData.transportFee) || 0)
          : 0,
      };

      if (editingService) {
        await updateDoc(doc(db, 'services', editingService.id), {
          ...serviceData, updatedAt: serverTimestamp()
        });
      } else {
        await addDoc(collection(db, 'services'), {
          ...serviceData, createdAt: serverTimestamp()
        });
      }

      setShowModal(false);
      resetForm();
      loadServices();
    } catch (error) {
      console.error('Error saving service:', error);
      alert(`Failed to save service: ${error.code || ''} ${error.message || ''}`);
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '', category: '', duration: '45 min', price: '',
      variablePrice: false, description: '', addons: [],
      paymentPolicy: 'inherit',
      depositPercent: 30,
      hasOptions: false,
      options: [],
      locationMode: 'salon',
      transportFee: 0,
    });
    setNewAddonName('');
    setNewAddonPrice('');
    setMediaItems([]);
    setEditingService(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this service?')) {
      try {
        await deleteDoc(doc(db, 'services', id));
        loadServices();
      } catch (error) {
        console.error('Error deleting service:', error);
      }
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);

    const normalizedOptions = Array.isArray(service.options)
      ? service.options.map(o => ({
          id: o.id || `opt_${Math.random().toString(36).slice(2, 8)}`,
          name: o.name || '',
          price: String(o.price ?? ''),
          duration: o.duration || service.duration || '45 min',
        }))
      : [];

    const normalizedAddons = Array.isArray(service.addons)
      ? service.addons.map(a => ({
          id: a.id || `addon_${Math.random().toString(36).slice(2, 8)}`,
          name: a.name || '',
          price: Number(a.price) || 0,
        }))
      : [];

    setFormData({
      title: service.title || '',
      category: service.category || '',
      duration: service.duration || '45 min',
      price: service.price || '',
      variablePrice: service.variablePrice || false,
      description: service.description || '',
      addons: normalizedAddons,
      paymentPolicy: service.paymentPolicy || 'inherit',
      depositPercent: service.depositPercent || 30,
      hasOptions: normalizedOptions.length > 0,
      options: normalizedOptions,
      locationMode: service.locationMode || 'salon',
      transportFee: service.transportFee || 0,
    });

    let loaded = [];
    if (Array.isArray(service.media)) {
      loaded = service.media.map(m => ({ url: m.url, type: m.type, isNew: false }));
    } else if (service.media?.url) {
      loaded = [{ url: service.media.url, type: service.media.type || 'image', isNew: false }];
    } else if (service.mediaLegacy?.url) {
      loaded = [{ url: service.mediaLegacy.url, type: service.mediaLegacy.type || 'image', isNew: false }];
    }
    setMediaItems(loaded);
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#CDBCE7] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const getCoverMedia = (service) => {
    if (Array.isArray(service.media) && service.media.length > 0) return service.media[0];
    if (service.media?.url) return service.media;
    if (service.mediaLegacy?.url) return service.mediaLegacy;
    return null;
  };

  const getPolicyLabel = (service) => {
    const policy = service.paymentPolicy || 'inherit';
    const effective = policy === 'inherit' ? proDefaultPolicy : policy;
    if (effective === 'after') return 'Pay after';
    if (effective === 'full') return 'Pay upfront';
    if (effective === 'deposit') {
      const pct = service.depositPercent || proDefaultDeposit;
      return `${pct}% deposit`;
    }
    return 'Pay after';
  };

  const getPolicyIcon = (service) => {
    const policy = service.paymentPolicy || 'inherit';
    const effective = policy === 'inherit' ? proDefaultPolicy : policy;
    if (effective === 'full') return <Wallet size={10} />;
    if (effective === 'deposit') return <CreditCard size={10} />;
    return <Banknote size={10} />;
  };

  const getLocationLabel = (service) => {
    const mode = service.locationMode || 'salon';
    if (mode === 'mobile') return 'Mobile';
    if (mode === 'both') return 'Salon · Mobile';
    return 'Salon';
  };

  const getLocationIcon = (service) => {
    const mode = service.locationMode || 'salon';
    if (mode === 'mobile') return <Car size={10} />;
    if (mode === 'both') return <MapPin size={10} />;
    return <Store size={10} />;
  };

  const livePrice = parseFloat(formData.price) || 0;
  const livePolicy = getEffectivePolicy(formData);
  const liveDepositPercent = getEffectiveDepositPercent(formData);
  const liveDepositAmount = Math.round(livePrice * (liveDepositPercent / 100));
  const liveBalance = livePrice - liveDepositAmount;

  const addonsTotal = (formData.addons || []).reduce((s, a) => s + (Number(a.price) || 0), 0);

  return (
    <div className="max-w-6xl mx-auto pb-24 sm:pb-8">
      {/* HEADER */}
      <div className="bg-[#F4EEFF] rounded-3xl p-6 md:p-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
          <div>
            <span className="text-[10px] font-bold tracking-[0.2em] text-[#AF8BD4] uppercase">Your Menu</span>
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-gray-900 mt-2">Services</h1>
            <p className="text-gray-500 mt-1.5 text-sm">
              {services.length > 0
                ? `${services.length} ${services.length === 1 ? 'service' : 'services'} on your menu`
                : 'Build your service menu'}
            </p>
          </div>
          
          {/* Main Desktop Button (Becomes full-width and larger on Mobile) */}
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="w-full sm:w-auto px-6 py-4 sm:py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-transform active:scale-[0.98] flex items-center justify-center gap-2 shadow-md touch-manipulation"
          >
            <Plus size={18} /> Add Service
          </button>
        </div>

        {services.length > 0 && (
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            <div className="bg-white rounded-2xl p-3 md:p-4">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Services</span>
              <p className="text-xl md:text-2xl font-serif font-bold text-gray-900">{services.length}</p>
            </div>
            <div className="bg-white rounded-2xl p-3 md:p-4">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Avg Price</span>
              <p className="text-xl md:text-2xl font-serif font-bold text-gray-900">
                R{Math.round(services.reduce((s, x) => s + (x.price || 0), 0) / (services.length || 1))}
              </p>
            </div>
            <div className="bg-white rounded-2xl p-3 md:p-4">
              <div className="flex items-center gap-2 mb-1">
                {getSpecializationIcon()}
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Specialty</span>
              </div>
              <p className="text-sm md:text-base font-bold text-gray-900 truncate">
                {userData?.specialization || 'General'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* MOBILE FLOATING ACTION BUTTON (Visible only on mobile when there are services) */}
      {services.length > 0 && (
        <button
          onClick={() => { resetForm(); setShowModal(true); }}
          className="sm:hidden fixed bottom-[88px] right-5 w-14 h-14 bg-[#010101] text-white rounded-full shadow-2xl flex items-center justify-center z-40 active:scale-95 transition-transform touch-manipulation"
          aria-label="Add Service"
        >
          <Plus size={24} />
        </button>
      )}

      {/* SERVICES LIST */}
      {services.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-gray-50 px-4">
          <div className="w-24 h-24 bg-[#F4EEFF] rounded-full flex items-center justify-center mx-auto mb-5">
            {getSpecializationIcon()}
          </div>
          <h3 className="text-xl font-serif font-bold text-gray-900 mb-2">Your menu is empty</h3>
          <p className="text-gray-400 text-sm mb-6 max-w-sm mx-auto">
            Add your services so clients can book you directly on Skytone.
          </p>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="w-full sm:w-auto px-6 py-4 sm:py-3 bg-[#010101] text-white rounded-full text-sm font-bold hover:bg-black/90 transition-transform active:scale-[0.98] flex items-center justify-center gap-2 touch-manipulation shadow-md"
          >
            <Plus size={18} /> Add Your First Service
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {services.map((service) => {
            const cover = getCoverMedia(service);
            const mediaCount = Array.isArray(service.media) ? service.media.length : (cover ? 1 : 0);
            const optionCount = Array.isArray(service.options) ? service.options.length : 0;
            const addonCount = Array.isArray(service.addons) ? service.addons.length : 0;
            return (
              <div
                key={service.id}
                className="bg-white p-4 rounded-3xl border border-gray-100 hover:border-[#CDBCE7] transition-colors flex items-center justify-between gap-4 group"
              >
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  <div className="relative w-16 h-16 rounded-2xl overflow-hidden bg-[#FCF8F9] flex items-center justify-center shrink-0">
                    {cover ? (
                      cover.type === 'video' ? (
                        <Video size={20} className="text-gray-400" />
                      ) : (
                        <img src={cover.url} className="w-full h-full object-cover" alt={service.title} />
                      )
                    ) : (
                      <div className="text-[#AF8BD4]">{getSpecializationIcon()}</div>
                    )}
                    {mediaCount > 1 && (
                      <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">
                        +{mediaCount - 1}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    {service.category && (
                      <span className="text-[10px] font-bold tracking-wider text-[#AF8BD4] uppercase">{service.category}</span>
                    )}
                    <div className="font-bold text-gray-900 text-[15px] truncate">{service.title}</div>
                    <div className="flex items-center gap-3 text-[11px] font-medium text-gray-500 mt-1 flex-wrap">
                      <span className="flex items-center gap-1"><Clock size={12} /> {service.duration || '45 min'}</span>
                      <span>•</span>
                      <span className="text-gray-900 font-bold">
                        {service.variablePrice ? 'From ' : ''}R{service.price || 0}
                        {optionCount > 0 && ` · ${optionCount} option${optionCount > 1 ? 's' : ''}`}
                        {addonCount > 0 && ` · ${addonCount} add-on${addonCount > 1 ? 's' : ''}`}
                      </span>
                      <span>•</span>
                      <span className="inline-flex items-center gap-1 text-[#AF8BD4] font-bold">
                        {getPolicyIcon(service)}
                        {getPolicyLabel(service)}
                      </span>
                      <span>•</span>
                      <span className="inline-flex items-center gap-1 text-[#AF8BD4] font-bold">
                        {getLocationIcon(service)}
                        {getLocationLabel(service)}
                        {service.transportFee > 0 && ` +R${service.transportFee}`}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Made edit/delete buttons larger touch targets for mobile */}
                <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                  <button
                    onClick={() => handleEdit(service)}
                    className="w-10 h-10 sm:w-9 sm:h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-500 hover:bg-[#F4EEFF] hover:text-[#AF8BD4] transition-colors active:scale-95 touch-manipulation"
                    aria-label="Edit Service"
                  >
                    <Settings size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(service.id)}
                    className="w-10 h-10 sm:w-9 sm:h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors active:scale-95 touch-manipulation"
                    aria-label="Delete Service"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50">
          <div className="bg-white rounded-t-3xl sm:rounded-3xl max-w-3xl w-full max-h-[95vh] overflow-hidden flex flex-col sm:flex-row">

            {/* LEFT: MEDIA PANEL */}
            <div className="sm:w-[42%] bg-[#F4EEFF] p-4 sm:p-5 flex flex-col shrink-0">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[10px] font-bold tracking-[0.2em] text-[#AF8BD4] uppercase">
                  Media · {mediaItems.length}/{MAX_MEDIA}
                </span>
                {/* Increased modal close button size for mobile */}
                <button
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="sm:hidden w-10 h-10 rounded-full bg-white flex items-center justify-center active:scale-95 touch-manipulation shadow-sm"
                >
                  <X size={20} className="text-gray-700" />
                </button>
              </div>

              <div className="relative w-full rounded-2xl overflow-hidden bg-white mb-3 aspect-[4/3] max-h-[180px]">
                {mediaItems[0] ? (
                  <>
                    {mediaItems[0].type === 'video' ? (
                      <video src={mediaItems[0].url} className="w-full h-full object-cover" muted playsInline />
                    ) : (
                      <img src={mediaItems[0].url} className="w-full h-full object-cover" alt="Cover" />
                    )}
                    <span className="absolute top-2 left-2 bg-[#010101] text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                      Cover
                    </span>
                    <button
                      type="button"
                      onClick={() => removeMedia(0)}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md active:scale-95 touch-manipulation"
                    >
                      <X size={14} />
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-full flex flex-col items-center justify-center text-[#AF8BD4] hover:bg-[#FCF8F9] transition-colors active:scale-[0.98] touch-manipulation"
                  >
                    <Camera size={24} />
                    <span className="text-[11px] font-bold uppercase tracking-widest mt-2">Add Media</span>
                    <span className="text-[10px] text-gray-400 mt-0.5">Up to {MAX_MEDIA} files</span>
                  </button>
                )}
              </div>

              <div className="flex flex-wrap gap-2">
                {mediaItems.slice(1).map((item, i) => {
                  const realIndex = i + 1;
                  return (
                    <div key={realIndex} className="relative w-14 h-14 sm:w-12 sm:h-12 rounded-lg overflow-hidden bg-white shrink-0">
                      <button
                        type="button"
                        onClick={() => makeCover(realIndex)}
                        className="w-full h-full active:scale-95 touch-manipulation"
                        title="Set as cover"
                      >
                        {item.type === 'video' ? (
                          <div className="w-full h-full flex items-center justify-center bg-[#FCF8F9]">
                            <Video size={16} className="text-gray-500" />
                          </div>
                        ) : (
                          <img src={item.url} className="w-full h-full object-cover" alt="" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeMedia(realIndex); }}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[9px] active:scale-95 touch-manipulation shadow-sm"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  );
                })}

                {mediaItems.length < MAX_MEDIA && mediaItems.length > 0 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-14 h-14 sm:w-12 sm:h-12 rounded-lg border-2 border-dashed border-[#CDBCE7] flex items-center justify-center text-[#AF8BD4] hover:bg-white transition-colors shrink-0 active:scale-95 touch-manipulation"
                  >
                    <Plus size={18} />
                  </button>
                )}
              </div>

              {mediaItems.length > 1 && (
                <p className="text-[10px] text-gray-500 mt-2">Tap a thumbnail to set as cover</p>
              )}

              <p className="text-[9px] text-gray-400 mt-3 leading-relaxed">
                Images are auto-compressed before upload for faster loading.
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFilesSelect}
                className="hidden"
              />
            </div>

            {/* RIGHT: FORM */}
            <div className="sm:w-[58%] flex flex-col max-h-[95vh] sm:max-h-[90vh] min-h-0 bg-white">
              <div className="p-5 sm:p-6 pb-4 border-b border-gray-50 flex items-center justify-between shrink-0">
                <div>
                  <h2 className="font-serif font-bold text-xl sm:text-2xl text-gray-900">
                    {editingService ? 'Edit Service' : 'New Service'}
                  </h2>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {editingService ? 'Update details below' : 'Fill in the details below'}
                  </p>
                </div>
                <button
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="hidden sm:flex w-9 h-9 rounded-full bg-[#FCF8F9] items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-5 sm:p-6 space-y-5 overflow-y-auto flex-1 min-h-0">
                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Service Name *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g. Volume Lashes"
                    className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-2">Category</label>
                  <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
                    {categoryOptions.map(cat => (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setFormData({ ...formData, category: cat })}
                        className={`px-4 py-2.5 rounded-full text-[11px] font-bold whitespace-nowrap transition-colors border touch-manipulation ${
                          formData.category === cat
                            ? 'bg-[#010101] text-white border-[#010101]'
                            : 'bg-white text-gray-500 border-gray-200 hover:border-[#CDBCE7]'
                        }`}
                      >
                        {cat}
                      </button>
                    ))}
                  </div>
                </div>

                {!formData.hasOptions && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Price (R) *</label>
                      <input
                        type="number"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        placeholder="299"
                        className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors"
                        required={!formData.hasOptions}
                      />
                    </div>
                    <div>
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Duration</label>
                      <select
                        value={formData.duration}
                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                        className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors cursor-pointer"
                      >
                        {durations.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                    </div>
                  </div>
                )}

                <div className="border-t border-gray-100 pt-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Sparkles size={14} className="text-[#AF8BD4]" />
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">
                        Service Options
                      </label>
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, hasOptions: !formData.hasOptions, options: formData.hasOptions ? [] : formData.options })}
                      className={`w-12 h-7 rounded-full transition-colors relative flex items-center p-1 touch-manipulation ${
                        formData.hasOptions ? 'bg-[#AF8BD4]' : 'bg-gray-200'
                      }`}
                    >
                      <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                        formData.hasOptions ? 'translate-x-5' : 'translate-x-0'
                      }`}></div>
                    </button>
                  </div>

                  {formData.hasOptions ? (
                    <>
                      <p className="text-[11px] text-gray-500 mb-3">
                        Let clients pick from a set of variants. Each option has its own price and duration.
                      </p>

                      <div className="space-y-3">
                        {formData.options.map((opt, idx) => (
                          <div key={opt.id} className="bg-[#FCF8F9] rounded-xl p-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                                Option {idx + 1}
                              </span>
                              {formData.options.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeOption(opt.id)}
                                  className="w-8 h-8 rounded-full bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 touch-manipulation active:scale-95"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <input
                                type="text"
                                value={opt.name}
                                onChange={(e) => updateOption(opt.id, 'name', e.target.value)}
                                placeholder="e.g. Classic"
                                className="col-span-2 bg-white border border-transparent rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7]"
                              />
                              <input
                                type="number"
                                value={opt.price}
                                onChange={(e) => updateOption(opt.id, 'price', e.target.value)}
                                placeholder="Price R"
                                className="bg-white border border-transparent rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7]"
                              />
                              <select
                                value={opt.duration}
                                onChange={(e) => updateOption(opt.id, 'duration', e.target.value)}
                                className="bg-white border border-transparent rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7] cursor-pointer"
                              >
                                {durations.map(d => <option key={d} value={d}>{d}</option>)}
                              </select>
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={addOption}
                        className="w-full mt-3 py-3.5 rounded-xl border-2 border-dashed border-[#CDBCE7] text-[#AF8BD4] text-[12px] font-bold hover:bg-[#F4EEFF] transition-colors flex items-center justify-center gap-1.5 touch-manipulation active:scale-[0.98]"
                      >
                        <Plus size={16} /> Add another option
                      </button>
                    </>
                  ) : (
                    <p className="text-[11px] text-gray-400">
                      Turn on to offer variants like Classic / Hybrid / Volume with different prices.
                    </p>
                  )}
                </div>

                <div className="border-t border-gray-100 pt-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Plus size={14} className="text-[#AF8BD4]" />
                      <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">
                        Add-ons
                      </label>
                    </div>
                    {(formData.addons || []).length > 0 && (
                      <span className="text-[10px] font-bold text-[#AF8BD4]">
                        +R{addonsTotal.toFixed(0)} total
                      </span>
                    )}
                  </div>

                  <p className="text-[11px] text-gray-500 mb-3">
                    Extras clients can add at booking — extra length, hot stones, treatments, etc.
                  </p>

                  {suggestedAddons.length > 0 && (
                    <div className="mb-4">
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                        Suggested for {userData?.specialization || 'you'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {suggestedAddons.map(s => {
                          const already = (formData.addons || []).some(a =>
                            a.name.toLowerCase() === s.name.toLowerCase()
                          );
                          return (
                            <button
                              key={s.name}
                              type="button"
                              onClick={() => !already && addSuggestedAddon(s)}
                              disabled={already}
                              className={`px-3 py-2 rounded-full text-[11px] font-bold transition-colors border touch-manipulation ${
                                already
                                  ? 'bg-[#F4EEFF] text-[#AF8BD4] border-[#CDBCE7] cursor-default'
                                  : 'bg-white text-gray-600 border-gray-200 hover:border-[#CDBCE7] hover:bg-[#FCF8F9] active:scale-95'
                              }`}
                            >
                              {already ? '✓ ' : '+ '}
                              {s.name} <span className="opacity-60">R{s.defaultPrice}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {(formData.addons || []).length > 0 && (
                    <div className="space-y-3 mb-4">
                      {(formData.addons || []).map((addon, idx) => (
                        <div key={addon.id} className="bg-[#F4EEFF] rounded-xl p-3 flex items-center gap-2">
                          <div className="flex-1 min-w-0 grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={addon.name}
                              onChange={(e) => updateAddon(addon.id, 'name', e.target.value)}
                              placeholder="Add-on name"
                              className="col-span-2 bg-white border border-transparent rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#CDBCE7]"
                            />
                            <div className="col-span-2 flex items-center gap-2">
                              <input
                                type="number"
                                value={addon.price}
                                onChange={(e) => updateAddon(addon.id, 'price', e.target.value)}
                                placeholder="Price"
                                min="0"
                                className="flex-1 bg-white border border-transparent rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#CDBCE7]"
                              />
                              <span className="text-[12px] font-bold text-[#AF8BD4] shrink-0">R</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeAddon(addon.id)}
                            className="w-10 h-10 rounded-full bg-white text-red-500 flex items-center justify-center hover:bg-red-50 shrink-0 touch-manipulation active:scale-95"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="bg-[#FCF8F9] rounded-xl p-4">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                      Add custom
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newAddonName}
                        onChange={(e) => setNewAddonName(e.target.value)}
                        placeholder="e.g. Nail art"
                        className="flex-1 bg-white border border-transparent rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7]"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCustomAddonSubmit();
                          }
                        }}
                      />
                      <input
                        type="number"
                        value={newAddonPrice}
                        onChange={(e) => setNewAddonPrice(e.target.value)}
                        placeholder="R"
                        min="0"
                        className="w-20 bg-white border border-transparent rounded-lg px-3 py-3 text-sm focus:outline-none focus:border-[#CDBCE7]"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleCustomAddonSubmit();
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={handleCustomAddonSubmit}
                        className="px-4 rounded-lg bg-[#AF8BD4] text-white font-bold text-sm hover:bg-[#9C77C9] transition-colors shrink-0 touch-manipulation active:scale-95"
                      >
                        Add
                      </button>
                    </div>
                  </div>

                  {(formData.addons || []).length > 0 && (
                    <p className="text-[10px] text-gray-400 mt-3">
                      Client sees these as optional extras — you keep the full add-on price.
                    </p>
                  )}
                </div>

                <div className="border-t border-gray-100 pt-5">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin size={14} className="text-[#AF8BD4]" />
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">
                      Where does the service happen?
                    </label>
                  </div>

                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, locationMode: 'salon' })}
                      className={`w-full text-left p-4 rounded-xl border transition-colors flex items-start gap-3 touch-manipulation active:scale-[0.98] ${
                        formData.locationMode === 'salon'
                          ? 'border-[#AF8BD4] bg-[#F4EEFF]'
                          : 'border-gray-100 hover:border-[#CDBCE7]'
                      }`}
                    >
                      <Store size={18} className="text-[#AF8BD4] shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-gray-900">Client comes to me</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          You provide the service at your salon/studio
                        </p>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, locationMode: 'mobile' })}
                      className={`w-full text-left p-4 rounded-xl border transition-colors flex items-start gap-3 touch-manipulation active:scale-[0.98] ${
                        formData.locationMode === 'mobile'
                          ? 'border-[#AF8BD4] bg-[#F4EEFF]'
                          : 'border-gray-100 hover:border-[#CDBCE7]'
                      }`}
                    >
                      <Car size={18} className="text-[#AF8BD4] shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-gray-900">I go to the client</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          You travel to the client's location
                        </p>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, locationMode: 'both' })}
                      className={`w-full text-left p-4 rounded-xl border transition-colors flex items-start gap-3 touch-manipulation active:scale-[0.98] ${
                        formData.locationMode === 'both'
                          ? 'border-[#AF8BD4] bg-[#F4EEFF]'
                          : 'border-gray-100 hover:border-[#CDBCE7]'
                      }`}
                    >
                      <MapPin size={18} className="text-[#AF8BD4] shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-gray-900">Both</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Client chooses at booking
                        </p>
                      </div>
                    </button>
                  </div>

                  {(formData.locationMode === 'mobile' || formData.locationMode === 'both') && (
                    <div className="mt-3 bg-[#FCF8F9] rounded-xl p-4">
                      <label className="text-[11px] font-bold text-gray-700 block mb-2">
                        Transport fee (R)
                      </label>
                      <input
                        type="number"
                        value={formData.transportFee}
                        onChange={(e) => setFormData({ ...formData, transportFee: e.target.value })}
                        placeholder="0"
                        min="0"
                        className="w-full bg-white border border-transparent rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-[#CDBCE7]"
                      />
                      <p className="text-[10px] text-gray-500 mt-2">
                        Added to the client's total when they choose mobile service. Leave 0 for no fee.
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-between bg-[#FCF8F9] rounded-xl px-5 py-4">
                  <div>
                    <div className="font-bold text-[13px] text-gray-900">Variable Pricing</div>
                    <div className="text-[11px] text-gray-500">Show "Starts at" on your profile</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, variablePrice: !formData.variablePrice })}
                    className={`w-12 h-7 rounded-full transition-colors relative flex items-center p-1 touch-manipulation ${
                      formData.variablePrice ? 'bg-[#AF8BD4]' : 'bg-gray-200'
                    }`}
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow-sm transition-transform ${
                      formData.variablePrice ? 'translate-x-5' : 'translate-x-0'
                    }`}></div>
                  </button>
                </div>

                <div className="border-t border-gray-100 pt-5">
                  <div className="flex items-center gap-2 mb-3">
                    <CreditCard size={14} className="text-[#AF8BD4]" />
                    <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">
                      Payment Policy
                    </label>
                  </div>

                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, paymentPolicy: 'after' })}
                      className={`w-full text-left p-4 rounded-xl border transition-colors flex items-start gap-3 touch-manipulation active:scale-[0.98] ${
                        formData.paymentPolicy === 'after'
                          ? 'border-[#AF8BD4] bg-[#F4EEFF]'
                          : 'border-gray-100 hover:border-[#CDBCE7]'
                      }`}
                    >
                      <Banknote size={18} className="text-[#AF8BD4] shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-gray-900">Pay after service</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Client pays {livePrice > 0 ? `R${livePrice.toFixed(2)}` : 'the full amount'} at their appointment
                        </p>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, paymentPolicy: 'deposit' })}
                      className={`w-full text-left p-4 rounded-xl border transition-colors flex items-start gap-3 touch-manipulation active:scale-[0.98] ${
                        formData.paymentPolicy === 'deposit'
                          ? 'border-[#AF8BD4] bg-[#F4EEFF]'
                          : 'border-gray-100 hover:border-[#CDBCE7]'
                      }`}
                    >
                      <CreditCard size={18} className="text-[#AF8BD4] shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-gray-900">Deposit required</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Client pays a deposit to secure the booking, remainder at appointment
                        </p>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, paymentPolicy: 'full' })}
                      className={`w-full text-left p-4 rounded-xl border transition-colors flex items-start gap-3 touch-manipulation active:scale-[0.98] ${
                        formData.paymentPolicy === 'full'
                          ? 'border-[#AF8BD4] bg-[#F4EEFF]'
                          : 'border-gray-100 hover:border-[#CDBCE7]'
                      }`}
                    >
                      <Wallet size={18} className="text-[#AF8BD4] shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-gray-900">Full payment upfront</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Client pays {livePrice > 0 ? `R${livePrice.toFixed(2)}` : 'the full amount'} before the appointment
                        </p>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, paymentPolicy: 'inherit' })}
                      className={`w-full text-left p-4 rounded-xl border transition-colors flex items-start gap-3 touch-manipulation active:scale-[0.98] ${
                        formData.paymentPolicy === 'inherit'
                          ? 'border-[#AF8BD4] bg-[#F4EEFF]'
                          : 'border-gray-100 hover:border-[#CDBCE7]'
                      }`}
                    >
                      <Info size={18} className="text-[#AF8BD4] shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-sm text-gray-900">Use my default</p>
                        <p className="text-[11px] text-gray-500 mt-0.5">
                          Currently: <span className="font-bold text-[#AF8BD4]">{proDefaultPolicy === 'after' ? 'Pay after' : proDefaultPolicy === 'full' ? 'Full upfront' : `${proDefaultDeposit}% deposit`}</span>
                        </p>
                      </div>
                    </button>
                  </div>

                  {(formData.paymentPolicy === 'deposit' ||
                    (formData.paymentPolicy === 'inherit' && proDefaultPolicy === 'deposit')) && (
                    <div className="mt-3 bg-[#FCF8F9] rounded-xl p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-[12px] font-bold text-gray-700">Deposit percentage</span>
                        <span className="text-[12px] font-bold text-[#AF8BD4]">
                          {formData.paymentPolicy === 'deposit' ? formData.depositPercent : proDefaultDeposit}%
                        </span>
                      </div>
                      {formData.paymentPolicy === 'deposit' ? (
                        <input
                          type="range"
                          min="10"
                          max="100"
                          step="5"
                          value={formData.depositPercent}
                          onChange={(e) => setFormData({ ...formData, depositPercent: parseInt(e.target.value) })}
                          className="w-full accent-[#AF8BD4]"
                        />
                      ) : (
                        <p className="text-[11px] text-gray-500">
                          Using profile default.
                        </p>
                      )}
                    </div>
                  )}

                  {livePrice > 0 && (
                    <div className="mt-3 bg-[#F4EEFF] rounded-xl p-4">
                      <p className="text-[10px] font-bold text-[#AF8BD4] uppercase tracking-wider mb-2">
                        Client will see
                      </p>
                      {livePolicy === 'after' && (
                        <p className="text-[13px] text-gray-700">
                          No payment now. <span className="font-bold">R{livePrice.toFixed(2)}</span> due at appointment.
                        </p>
                      )}
                      {livePolicy === 'full' && (
                        <p className="text-[13px] text-gray-700">
                          Pay <span className="font-bold">R{livePrice.toFixed(2)}</span> to secure booking.
                        </p>
                      )}
                      {livePolicy === 'deposit' && (
                        <p className="text-[13px] text-gray-700">
                          Pay <span className="font-bold">R{liveDepositAmount.toFixed(2)}</span> now,{' '}
                          <span className="font-bold">R{liveBalance.toFixed(2)}</span> at appointment.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">Description</label>
                  <textarea
                    rows="4"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what's included in this service..."
                    className="w-full bg-[#FCF8F9] border border-transparent rounded-xl px-4 py-3.5 text-sm focus:outline-none focus:border-[#CDBCE7] focus:bg-white transition-colors resize-none"
                  />
                </div>
              </form>

              <div className="p-5 sm:p-6 pt-4 border-t border-gray-50 shrink-0">
                <button
                  onClick={handleSubmit}
                  disabled={uploading}
                  className="w-full bg-[#010101] text-white py-4 sm:py-3.5 rounded-full font-bold text-sm hover:bg-black/90 transition-transform active:scale-[0.98] touch-manipulation disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {uploading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>{editingService ? 'Save Changes' : 'Publish Service'}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfessionalServicesPage;